import React, { useEffect, useState, useRef, FormEvent } from 'react';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import OnboardingCreateAccountSVG from '@birdi/icons/onboarding-create-account-2.svg';
import EmailIcon from '@birdi/icons/onboarding-at.svg';
import LockIcon from '@birdi/icons/onboarding-lock.svg';
import { GoogleAuthButton } from './google-auth-button';
// @ts-ignore - JS file without types
import ErrorDisplay from '../../common/ErrorDisplay';
// @ts-ignore - JS file without types
import { updateField, signInWithPassword } from '../actions';
import { saveAuthSearchParams } from '../utils';
import * as styles from './styles';
import { AuthState } from '../reducer';
import { sso, stroot } from '@birdi/js-sdk';

type Stage = 'email' | 'password';

interface SignInProps {
  auth: AuthState;
  _updateField: (field: Partial<AuthState>) => void;
  _signInWithPassword: (obj: { email: string; password: string }) => void;
}

const SignIn: React.FC<SignInProps> = (props) => {
  const theme = useTheme() as BirdiTheme;
  const [stage, setStage] = useState<Stage>('email');
  const [password, setPassword] = useState('');
  const [ssoChecking, setSsoChecking] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setPassword('');
    saveAuthSearchParams();
  }, []);

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formRef.current?.checkValidity()) {
      return formRef.current?.reportValidity();
    }
    setSsoChecking(true);
    try {
      const result = await sso.checkSSO(stroot('ugh9oh'), props.auth.email);
      if (result.body?.sso && result.body.authorizationUrl) {
        window.location.href = result.body.authorizationUrl;
      } else {
        setStage('password');
      }
    } catch {
      setStage('password');
    } finally {
      setSsoChecking(false);
    }
  };

  const handleSignIn = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formRef.current?.checkValidity()) {
      return formRef.current?.reportValidity();
    }
    return props._signInWithPassword({ email: props.auth.email, password });
  };

  const handleBack = () => {
    setStage('email');
    setPassword('');
  };

  return (
    <>
      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <OnboardingCreateAccountSVG />
            </div>
            <h1 css={styles.title}>Welcome back - Sign in to Birdi</h1>
          </div>
          <p css={styles.subtitle}>
            Catch up on what's new across your team's maps, projects, and
            geospatial work.
          </p>
        </div>

        {stage === 'email' ? (
          /* Email stage */
          <form
            ref={formRef}
            onSubmit={handleEmailSubmit}
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0.25rem;

              @media (min-width: ${breakpoints.s}) {
                gap: 0.5rem;
              }
            `}
          >
            <div css={styles.formField}>
              <label css={styles.label} htmlFor="email">
                Email address <span className="required">*</span>
              </label>
              <div css={styles.inputWrapper}>
                <div css={styles.inputIcon}>
                  <EmailIcon />
                </div>
                <input
                  id="email"
                  type="email"
                  css={styles.input}
                  placeholder="Enter your email"
                  value={props.auth.email}
                  onChange={(event) =>
                    props._updateField({ email: event.target.value })
                  }
                  required
                  autoFocus
                />
              </div>
              <div
                css={(theme: BirdiTheme) => css`
                  ${styles.errorMessage};
                  a {
                    color: ${theme.deepOcean};
                    font-weight: 500;
                  }
                `}
              >
                <ErrorDisplay error={props.auth.error} path="email" />
              </div>
            </div>

            <div css={styles.buttonContainer}>
              <button
                type="submit"
                css={styles.primaryButton}
                disabled={ssoChecking}
              >
                {ssoChecking ? 'Checking...' : 'Continue'}
              </button>
              <GoogleAuthButton label="Sign in with Google" />
            </div>

            <div
              css={(theme: BirdiTheme) => css`
                text-align: left;
                margin-top: 0.5rem;
                font-size: 0.9375rem;
                color: ${theme.mono70};
                flex-shrink: 0;
              `}
            >
              Don't have an account?{' '}
              <a
                href="https://www.birdi.io/get-access"
                target="_blank"
                rel="noopener noreferrer"
                css={(theme: BirdiTheme) => css`
                  color: ${theme.deepOcean};
                  text-decoration: none;
                  font-weight: 500;

                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                Request access
              </a>
            </div>
          </form>
        ) : (
          /* Password stage */
          <form
            ref={formRef}
            onSubmit={handleSignIn}
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0.25rem;

              @media (min-width: ${breakpoints.s}) {
                gap: 0.5rem;
              }
            `}
          >
            {/* Static email display with Back button */}
            <div css={styles.formField}>
              <div
                css={(theme: BirdiTheme) => css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  gap: 0.5rem;
                `}
              >
                <span
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.9375rem;
                    color: ${theme.midnight};
                    font-weight: 500;
                    word-break: break-all;
                  `}
                >
                  {props.auth.email}
                </span>
                <button
                  type="button"
                  onClick={handleBack}
                  css={(theme: BirdiTheme) => css`
                    background: none;
                    border: none;
                    padding: 0;
                    font-size: 0.875rem;
                    color: ${theme.deepOcean};
                    font-weight: 500;
                    cursor: pointer;
                    white-space: nowrap;
                    flex-shrink: 0;

                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                >
                  Back
                </button>
              </div>
            </div>

            {/* Password */}
            <div css={styles.formField}>
              <label css={styles.label} htmlFor="password">
                Password <span className="required">*</span>
              </label>
              <div css={styles.inputWrapper}>
                <div css={styles.inputIcon}>
                  <LockIcon />
                </div>
                <input
                  id="password"
                  type="password"
                  css={styles.input}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div
                css={(theme: BirdiTheme) => css`
                  ${styles.errorMessage};
                  a {
                    color: ${theme.deepOcean};
                    font-weight: 500;
                  }
                `}
              >
                <ErrorDisplay error={props.auth.error} path="password" />
              </div>
            </div>

            {/* Forgot password + form-level errors (network, email verification, etc.) */}
            <div
              css={(theme: BirdiTheme) => css`
                ${styles.errorMessage};
                a {
                  color: ${theme.deepOcean};
                  font-weight: 500;
                }
              `}
            >
              <ErrorDisplay error={props.auth.error} path="meta" />
            </div>

            {/* Forgot Password Link */}
            <div
              css={css`
                margin-top: 0.5rem;
                margin-bottom: 0.5rem;
              `}
            >
              <Link
                to="/reset-password"
                css={(theme: BirdiTheme) => css`
                  font-size: 0.875rem;
                  color: ${theme.deepOcean};
                  text-decoration: none;
                  font-weight: 500;

                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                Forgot password?
              </Link>
            </div>

            <div css={styles.buttonContainer}>
              <button type="submit" css={styles.primaryButton}>
                Log in
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    auth: state.auth,
  }),
  (dispatch: any) => ({
    _updateField: (field: Partial<AuthState>) => dispatch(updateField(field)),
    _signInWithPassword: (obj: { email: string; password: string }) =>
      dispatch(signInWithPassword(obj)),
  }),
)(SignIn);
